/**
 * Klassen zusammenfuehren — eine Fassung fuer die ganze Plattform.
 *
 * Hier stand bis zum 14.09.2026 eine zweite, eigene Umsetzung mit
 * dem unkonfigurierten `twMerge`. Die kannte unsere Schriftstufen
 * nicht und verwarf `text-body-sm` als vermeintlichen Konflikt mit
 * `text-text-secondary` — siehe die Begruendung in
 * packages/foundation/src/cn.ts. Zwei Fassungen derselben Funktion
 * sind ohnehin eine zu viel.
 */
export { cn } from "@ansichtssache/foundation";

import { houseToday } from "@/lib/zeit/arbeitszeit";

/**
 * Zahlen auf Deutsch — die Fassung steht in `number-format.ts`.
 * Hier bleibt nur der Ausgang, weil der halbe Bestand
 * `formatCurrency` aus `@/lib/utils` holt.
 */
export {
  formatCurrency,
  formatAmount,
  formatDecimal,
  formatQuantity,
  formatHoursDecimal,
  formatPercent,
  parseDecimal,
  parseAmountCents,
  toCanonical,
} from "./number-format";

/** Dezimalstunden → "5h 30m" */
export function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Die Zeitzone, in der das Haus lebt — fuer jede Anzeige einer
 * Uhrzeit. Sie steht ausdruecklich da, weil der Server in UTC
 * laeuft und der Browser in deutscher Zeit: ohne feste Zone
 * zeichnet der Server „10:47", der Browser beim Hydrieren „12:47",
 * und React verwirft den ganzen Baum (Hydration-Fehler).
 */
export const APP_TIME_ZONE = "Europe/Berlin";

/**
 * Datum formatieren — als Kalendertag in UTC, so wie die Daten in
 * der Datenbank liegen (`@db.Date` ist UTC-Mitternacht, Zeitraum-
 * Enden sind 23:59:59 UTC). In Berliner Zeit wuerde aus „bis
 * 31.12." ein „01.01.". Fest steht die Zone aus demselben Grund wie
 * oben: Server und Browser muessen denselben Tag zeichnen.
 */
export function formatDate(date: Date | string, locale = "de-DE"): string {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(date));
}

/**
 * Der Tag eines ZEITPUNKTS — angelegt, verschickt, entschieden,
 * zuletzt angemeldet. Anders als `formatDate` in der Zeitzone des
 * Hauses: was um 00:30 Berliner Zeit geschah, ist 22:30 UTC am
 * Vortag und stuende dort mit dem falschen Tag. Fuer Kalendertage
 * und Fristen (`@db.Date`, `dueAt`, Aufbewahrungsenden) bleibt
 * `formatDate` richtig.
 */
export function formatTimestampDate(date: Date | string, locale = "de-DE"): string {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: APP_TIME_ZONE,
  }).format(new Date(date));
}

/**
 * Datum MIT Uhrzeit — fuer Vorgaenge, bei denen die Stunde
 * zaehlt: wann eine Meldung kam, wann etwas abgeschlossen wurde.
 * Fuer alles Belegartige bleibt `formatDate` richtig; dort ist
 * die Uhrzeit Laerm.
 */
export function formatDateTime(date: Date | string, locale = "de-DE"): string {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: APP_TIME_ZONE,
  }).format(new Date(date));
}

/** Initialen aus Name */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * Ganze Tage bis zum Stichtag; negativ, wenn er vorbei ist.
 *
 * Gerechnet wird in UTC-Mitternacht, weil die Stichtage der
 * Belege (`@db.Date`) genau so in der Datenbank liegen. Die
 * Ortszeit dagegen verschoebe den Tag je nach Jahreszeit um eins.
 */
export function tageBis(datum: Date | string): number {
  const tag = 24 * 60 * 60 * 1000;
  const ziel = new Date(datum);
  // „Heute" ist der Tag des Hauses — auf dem Server (UTC) und im
  // Browser derselbe.
  const a = houseToday().getTime();
  const b = Date.UTC(ziel.getFullYear(), ziel.getMonth(), ziel.getDate());
  return Math.round((b - a) / tag);
}

/**
 * Ganze Tage SEIT einem Zeitpunkt; negativ, wenn er noch
 * bevorsteht. Das Gegenstueck zu `tageBis`.
 *
 * Eigene Funktion und nicht `Date.now()` an der Aufrufstelle:
 * `react-hooks/purity` verbietet den direkten Griff zur Uhr
 * beim Rendern, und zu Recht — dieselbe Seite ergaebe sonst
 * bei jedem Rendern eine andere Zahl. Hier steht er EINMAL,
 * hinter einem Namen, der sagt, was er bedeutet.
 */
export function tageSeit(datum: Date | string): number {
  return -tageBis(datum);
}

/** „3 Tagen“, „1 Tag“ — die Einzahl stimmt mit, das Vorzeichen fällt weg. */
export function tageText(tage: number): string {
  const n = Math.abs(tage);
  return `${n} ${n === 1 ? "Tag" : "Tagen"}`;
}
